import React from 'react';
import { useTranslation } from 'react-i18next';
import '../assets/styles/TerraformArticle.scss';
import imageressource from '../assets/images/terraformeressource.png';
import imagecontainer from '../assets/images/terraformecontainer.png';
import imagefinal from '../assets/images/terraformeressourcefinal.png';


function TerraformArticle() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith('en');

  if (isEn) {
    return (
      <div className="terraform-article">
        <h1>Azure Resource Deployment with Terraform</h1>

        <section>
          <h2>1. 🔍 Project Context</h2>
          <p style={{ textAlign: 'center' }}>
            As part of my portfolio, I wanted to deploy a reproducible and maintainable cloud infrastructure. I chose Azure for hosting and Terraform for Infrastructure as Code (IaC) management. The goal was to create a stable foundation including essential resources: networking, security, secret management and virtual machine.
          </p>
        </section>

        <section>
          <h2>2. 🧐 Planned Global Architecture</h2>
          <p>The target infrastructure includes:</p>
          <ul>
            <li>A Virtual Network with a subnet</li>
            <li>A Network Security Group (NSG)</li>
            <li>An Azure Key Vault for secret management</li>
            <li>A Spot VM that will host Kubernetes and other services</li>
          </ul>
        </section>
        <div style={{ textAlign: 'center', margin: '10px 0' }}>
              <img src={imageressource} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
            </div>

        <section>
          <h2>3. 📁 Terraform Backend Configuration</h2>
          <p>To keep the Terraform state centralized and collaborative, I manually created a remote backend on Azure:</p>
          <ul>
            <li>Creating a Storage Account and a blob container</li>
            <li>Backend configuration in <code>provider.tf</code>:</li>
          </ul>
          <pre>
            <code>
              terraform &#123;<br />
                backend "azurerm" &#123;<br />
                  resource_group_name  = "magroupederessource"<br />
                  storage_account_name = "monstorageaccount"<br />
                  container_name       = "monconatinername"<br />
                  key                  = "nomdufichier"<br />
                &#125;<br />
              &#125;
            </code>
          </pre>
          <div style={{ textAlign: 'center', margin: '10px 0' }}>
              <img src={imagecontainer} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
            </div>
        </section>

        <section>
          <h2>4. 🔐 Authentication and Rights Management</h2>
          <p>To allow Terraform to interact with Azure, I created a Service Principal (SP) with the necessary rights:</p>
          <ul>
            <li>Assigning the Contributor role on the resource group</li>
            <li>Environment variables for authentication:</li>
          </ul>
            <pre>
              <code>
                 az ad sp create-for-rbac -n "api://sp-terraform-prod" --role="Contributor" --scopes="/subscriptions/xxxxx-xxxxxx-xxxxx-xxx" &gt; sp.json
              </code>
          </pre>
          <pre>
              <code>
                 less sp.json <br />

                  "appId": "xxxxxxxxxxxxxxxxxxxxx", <br />
                  "displayName": "api://sp-terraform-prod",<br />
                  "password": "xxxxxxxxxxxxxxxxxxxxxx",<br />
                  "tenant": "xxxxxxxxxxxxxxxxxxxxxxxxx"<br />
              </code>
          </pre>
          <pre>
            <code>
              export ARM_CLIENT_ID="xxxx"<br />
              export ARM_CLIENT_SECRET="xxxx"<br />
              export ARM_SUBSCRIPTION_ID="xxxx"<br />
              export ARM_TENANT_ID="xxxx"
            </code>
          </pre>
          {/* Insérez une image ici si vous le souhaitez */}
        </section>

        <section>
          <h2>5. 🌐 Azure Provider Configuration</h2>
          <p>The Azure provider is defined in <code>provider.tf</code>:</p>
          <pre>
            <code>
              provider "azurerm" &#123;<br />
                features &#123;&#125;<br />
              &#125;
            </code>
          </pre>
          <p>Sensitive values (resource name, location, etc.) are stored in a <code>variables.tf</code> file and injected via <code>terraform.tfvars</code>.</p>
          {/* Insérez une image ici si vous le souhaitez */}
        </section>

        <section>
          <h2>6. 🛠️ Main Resource Deployment</h2>
           <section>
              <h3> Resource Group </h3>
              <pre>
               <code>
                 resource "azurerm_resource_group" "rg-fanosit-prod" &#123; <br />
                 name     = var.resource_group_name-prod <br />
                 location = var.location-prod <br />
                 &#125;
               </code>
              </pre>
           </section>


          <section>
            <h3> Virtual Network (VNet) and Subnet</h3>
            <pre>
              <code>
                resource "azurerm_virtual_network" "main" &#123;<br />
                  name                = "vnet-projet"<br />
                  address_space       = &#91;"10.0.0.0/16"&#93;<br />
                  location            = var.location<br />
                  resource_group_name = var.resource_group_name<br />
                &#125;
              </code>
            </pre>
            {/* Exemple d'insertion d'image après le code du VNet */}
            <pre>
              <code>
                resource "azurerm_subnet" "subnet1" &#123;<br />
                  name            = "subnet-projet"<br />
                  resource_group_name = var.resource_group_name<br />
                  virtual_network_name = azurerm_virtual_network.main.name<br />
                  address_prefixes      = &#91;"10.0.1.0/24"&#93;<br />
                &#125;
              </code>
            </pre>
          </section>

          <section>
            <h3>  Network Security Group (NSG)</h3>
            <pre>
              <code>
                resource "azurerm_network_security_group" "main" &#123;<br />
                  name                = "nsg-projet"<br />
                  location            = var.location<br />
                  resource_group_name = var.resource_group_name<br />
                &#125;
              </code>
            </pre>
            {/* Insérez une image ici si vous le souhaitez */}
          </section>

          <section>
            <h3> Key Vault</h3>
            <pre>
              <code>
                resource "azurerm_key_vault" "main" &#123;<br />
                  name                        = "kv-projet"<br />
                  location                    = var.location<br />
                  resource_group_name         = var.resource_group_name<br />
                  tenant_id                   = var.tenant_id<br />
                  sku_name                    = "standard"<br />
                &#125;
              </code>
            </pre>
            {/* Insérez une image ici si vous le souhaitez */}
          </section>

          <section>
            <h3>  Virtual Machine (Spot VM)</h3>
            <pre>
              <code>
                resource "azurerm_linux_virtual_machine" "main" &#123;<br />
                  name                            = "vm-kube"<br />
                  location                        = var.location<br />
                  resource_group_name             = var.resource_group_name<br />
                  size                            = "Standard_B2s"<br />
                  admin_username                  = "azureuser"<br />
                  network_interface_ids           = &#91;azurerm_network_interface.main.id&#93;<br />
                  priority                        = "Spot"<br />
                  eviction_policy                 = "Deallocate"<br />
                  source_image_reference &#123;<br />
                    publisher = "Canonical"<br />
                    offer     = "UbuntuServer"<br />
                    sku       = "18.04-LTS"<br />
                    version   = "latest"<br />
                  &#125;<br />
                &#125;
              </code>
            </pre>
            <div style={{ textAlign: 'center', margin: '10px 0' }}>
              <img src={imagefinal} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
            </div>
          </section>
        </section>
      </div>
    );
  }

  return (
       <div className="terraform-article">
      <h1>Déploiement des ressources Azure via Terraform</h1>

      <section>
        <h2>1. 🔍 Contexte du projet</h2>
        <p style={{ textAlign: 'center' }}>
          Dans le cadre de mon portfolio, j'ai souhaité déployer une infrastructure cloud reproductible et maintenable. Mon choix s'est porté sur Azure pour l'hébergement, et sur Terraform pour la gestion de l'infrastructure en tant que code (IaC). L'objectif était de créer une base stable comprenant des ressources essentielles : réseau, sécurité, gestion de secrets et machine virtuelle.
        </p>
      </section>

      <section>
        <h2>2. 🧐 Architecture globale prévue</h2>
        <p>L'infrastructure cible comprend :</p>
        <ul>
          <li>Un Virtual Network avec un sous-réseau</li>
          <li>Un Network Security Group (NSG)</li>
          <li>Un Azure Key Vault pour la gestion des secrets</li>
          <li>Une machine virtuelle (VM) de type Spot, qui accueillera Kubernetes et d'autres services</li>
        </ul>
      </section>
      <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <img src={imageressource} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
          </div>

      <section>
        <h2>3. 📁 Configuration du backend Terraform</h2>
        <p>Afin de garder l'état de Terraform de manière centralisée et collaborative, j'ai créé manullement un backend distant sur Azure :</p>
        <ul>
          <li>Création d'un Storage Account et d'un container blob</li>
          <li>Configuration du backend dans <code>provider.tf</code> :</li>
        </ul>
        <pre>
          <code>
            terraform &#123;<br />
              backend "azurerm" &#123;<br />
                resource_group_name  = "magroupederessource"<br />
                storage_account_name = "monstorageaccount"<br />
                container_name       = "monconatinername"<br />
                key                  = "nomdufichier"<br />
              &#125;<br />
            &#125;
          </code>
        </pre>
        <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <img src={imagecontainer} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
          </div>
      </section>

      <section>
        <h2>4. 🔐 Authentification et gestion des droits</h2>
        <p>Pour permettre à Terraform d'interagir avec Azure, j'ai créé un Service Principal (SP) avec les droits nécessaires :</p>
        <ul>
          <li>Attribution du rôle Contributor sur le groupe de ressources</li>
          <li>Variables d'environnement pour l'authentification :</li>
        </ul>
          <pre>
            <code>
               az ad sp create-for-rbac -n "api://sp-terraform-prod" --role="Contributor" --scopes="/subscriptions/xxxxx-xxxxxx-xxxxx-xxx" &gt; sp.json
            </code>
        </pre>
        <pre>
            <code>
               less sp.json <br />

                "appId": "xxxxxxxxxxxxxxxxxxxxx", <br />
                "displayName": "api://sp-terraform-prod",<br />
                "password": "xxxxxxxxxxxxxxxxxxxxxx",<br />
                "tenant": "xxxxxxxxxxxxxxxxxxxxxxxxx"<br />
            </code>
        </pre>
        <pre>
          <code>
            export ARM_CLIENT_ID="xxxx"<br />
            export ARM_CLIENT_SECRET="xxxx"<br />
            export ARM_SUBSCRIPTION_ID="xxxx"<br />
            export ARM_TENANT_ID="xxxx"
          </code>
        </pre>
        {/* Insérez une image ici si vous le souhaitez */}
      </section>

      <section>
        <h2>5. 🌐 Configuration du provider Azure</h2>
        <p>Le provider Azure est défini dans <code>provider.tf</code> :</p>
        <pre>
          <code>
            provider "azurerm" &#123;<br />
              features &#123;&#125;<br />
            &#125;
          </code>
        </pre>
        <p>Les valeurs sensibles (nom de ressource, localisation, etc.) sont stockées dans un fichier <code>variables.tf</code> et injectées via <code>terraform.tfvars</code>.</p>
        {/* Insérez une image ici si vous le souhaitez */}
      </section>

      <section>
        <h2>6. 🛠️ Déploiement des ressources principales</h2>
         <section>
            <h3> Ressource groupe </h3>
            <pre>
             <code>
               resource "azurerm_resource_group" "rg-fanosit-prod" &#123; <br />
               name     = var.resource_group_name-prod <br />
               location = var.location-prod <br />
               &#125;
             </code>
            </pre>
         </section>


        <section>
          <h3> Réseau virtuel (VNet) et sous-réseau</h3>
          <pre>
            <code>
              resource "azurerm_virtual_network" "main" &#123;<br />
                name                = "vnet-projet"<br />
                address_space       = &#91;"10.0.0.0/16"&#93;<br />
                location            = var.location<br />
                resource_group_name = var.resource_group_name<br />
              &#125;
            </code>
          </pre>
          {/* Exemple d'insertion d'image après le code du VNet */}
          <pre>
            <code>
              resource "azurerm_subnet" "subnet1" &#123;<br />
                name            = "subnet-projet"<br />
                resource_group_name = var.resource_group_name<br />
                virtual_network_name = azurerm_virtual_network.main.name<br />
                address_prefixes      = &#91;"10.0.1.0/24"&#93;<br />
              &#125;
            </code>
          </pre>
        </section>

        <section>
          <h3>  Network Security Group (NSG)</h3>
          <pre>
            <code>
              resource "azurerm_network_security_group" "main" &#123;<br />
                name                = "nsg-projet"<br />
                location            = var.location<br />
                resource_group_name = var.resource_group_name<br />
              &#125;
            </code>
          </pre>
          {/* Insérez une image ici si vous le souhaitez */}
        </section>

        <section>
          <h3> Key Vault</h3>
          <pre>
            <code>
              resource "azurerm_key_vault" "main" &#123;<br />
                name                        = "kv-projet"<br />
                location                    = var.location<br />
                resource_group_name         = var.resource_group_name<br />
                tenant_id                   = var.tenant_id<br />
                sku_name                    = "standard"<br />
              &#125;
            </code>
          </pre>
          {/* Insérez une image ici si vous le souhaitez */}
        </section>

        <section>
          <h3>  Machine virtuelle (VM Spot)</h3>
          <pre>
            <code>
              resource "azurerm_linux_virtual_machine" "main" &#123;<br />
                name                            = "vm-kube"<br />
                location                        = var.location<br />
                resource_group_name             = var.resource_group_name<br />
                size                            = "Standard_B2s"<br />
                admin_username                  = "azureuser"<br />
                network_interface_ids           = &#91;azurerm_network_interface.main.id&#93;<br />
                priority                        = "Spot"<br />
                eviction_policy                 = "Deallocate"<br />
                source_image_reference &#123;<br />
                  publisher = "Canonical"<br />
                  offer     = "UbuntuServer"<br />
                  sku       = "18.04-LTS"<br />
                  version   = "latest"<br />
                &#125;<br />
              &#125;
            </code>
          </pre>
          <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <img src={imagefinal} alt="Schéma du réseau virtuel Azure" style={{ maxWidth: '80%', height: 'auto' }} />
          </div>
        </section>
      </section>
    </div>
  );
}
export default TerraformArticle;
